import React, { useState, useMemo } from "react";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Badge, 
  Modal,
  Alert,
  ProgressBar
} from "react-bootstrap";
import {
  Search,
  Eye,
  Download,
  Filter,
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Prescription,
  CheckCircle,
  Clock,
  Activity,
  GraphUp
} from "react-bootstrap-icons";
import { useAuth } from "../../contexts/AuthContext";

// Données fictives des ordonnances
const mockPrescriptions = [
  { 
    id: "ORD-001", 
    patientId: "CNAMGS-123456", 
    doctorId: "DOC-001", 
    doctorName: "Dr. Nzogho", 
    date: "2025-03-12", 
    medicines: [
      { id: "MED-001", name: "Paracétamol", dosage: "500mg", quantity: 10, instruction: "2 comprimés par jour après les repas" },
      { id: "MED-002", name: "Vitamine C", dosage: "1000mg", quantity: 20, instruction: "1 comprimé par jour" }
    ], 
    status: "pending",
    diagnosis: "Fièvre et maux de tête",
    notes: "Repos et hydratation"
  },
  { 
    id: "ORD-002", 
    patientId: "CNAMGS-123456", 
    doctorId: "DOC-002", 
    doctorName: "Dr. Mba", 
    date: "2025-03-01", 
    medicines: [
      { id: "MED-003", name: "Amoxicilline", dosage: "250mg", quantity: 15, instruction: "3 fois par jour pendant 5 jours" }
    ], 
    status: "dispensed",
    diagnosis: "Infection bactérienne",
    notes: "Terminer le traitement complet"
  },
  { 
    id: "ORD-003", 
    patientId: "CNAMGS-123456", 
    doctorId: "DOC-001", 
    doctorName: "Dr. Nzogho", 
    date: "2025-02-20", 
    medicines: [
      { id: "MED-004", name: "Ibuprofène", dosage: "400mg", quantity: 20, instruction: "1 comprimé après les repas" },
      { id: "MED-005", name: "Spasfon", dosage: "80mg", quantity: 12, instruction: "En cas de douleur" }
    ], 
    status: "reimbursed",
    diagnosis: "Douleurs musculaires",
    notes: "Éviter les efforts intenses"
  },
  { 
    id: "ORD-004", 
    patientId: "CNAMGS-123456", 
    doctorId: "DOC-003", 
    doctorName: "Dr. Ondo", 
    date: "2025-02-10", 
    medicines: [
      { id: "MED-006", name: "Doliprane", dosage: "1000mg", quantity: 8, instruction: "1 comprimé en cas de fièvre" }
    ], 
    status: "dispensed",
    diagnosis: "Fièvre légère",
    notes: "Prendre en cas de besoin"
  },
  { 
    id: "ORD-005", 
    patientId: "CNAMGS-123456", 
    doctorId: "DOC-002", 
    doctorName: "Dr. Mba", 
    date: "2025-01-25", 
    medicines: [
      { id: "MED-007", name: "Antadys", dosage: "200mg", quantity: 10, instruction: "1 comprimé matin et soir" }
    ], 
    status: "reimbursed",
    diagnosis: "Inflammation",
    notes: "À prendre avec les repas"
  }
];

const PrescriptionHistory = () => {
  const { user } = useAuth();
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);

  // Utiliser les données fictives
  const patientPrescriptions = mockPrescriptions.filter(p => p.patientId === "CNAMGS-123456");

  // Calcul des statistiques
  const stats = useMemo(() => ({
    total: patientPrescriptions.length,
    pending: patientPrescriptions.filter(p => p.status === 'pending').length,
    dispensed: patientPrescriptions.filter(p => p.status === 'dispensed').length,
    reimbursed: patientPrescriptions.filter(p => p.status === 'reimbursed').length,
    pendingRate: patientPrescriptions.length > 0 ? (patientPrescriptions.filter(p => p.status === 'pending').length / patientPrescriptions.length) * 100 : 0,
    dispensedRate: patientPrescriptions.length > 0 ? (patientPrescriptions.filter(p => p.status === 'dispensed').length / patientPrescriptions.length) * 100 : 0,
    reimbursedRate: patientPrescriptions.length > 0 ? (patientPrescriptions.filter(p => p.status === 'reimbursed').length / patientPrescriptions.length) * 100 : 0
  }), [patientPrescriptions]);

  // Filtrage
  const filteredPrescriptions = patientPrescriptions.filter(p => {
    const matchesSearch = filterText === '' || 
      p.doctorName.toLowerCase().includes(filterText.toLowerCase()) ||
      p.medicines.some(m => m.name.toLowerCase().includes(filterText.toLowerCase())) ||
      p.id.toLowerCase().includes(filterText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Tri
  const sortedPrescriptions = [...filteredPrescriptions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'date') {
      aValue = new Date(a.date);
      bValue = new Date(b.date);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge bg="warning" className="d-flex align-items-center px-2 py-1"><Clock className="me-1" size={12} /> En attente</Badge>,
      dispensed: <Badge bg="info" className="d-flex align-items-center px-2 py-1"><CheckCircle className="me-1" size={12} /> Délivrée</Badge>,
      reimbursed: <Badge bg="success" className="d-flex align-items-center px-2 py-1"><CheckCircle className="me-1" size={12} /> Remboursée</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => handleSort(field)}
      className="position-relative"
    >
      {children}
      {sortField === field && (
        <span className="ms-1">
          {sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      )}
    </th>
  );

  // Cartes de statistiques
  const statsCards = [
    {
      title: "Total Ordonnances",
      value: stats.total,
      icon: Prescription,
      color: "primary",
      badge: { text: "Prescriptions", icon: <FileText className="me-1" size={12} /> },
      progress: { value: 100, label: "Total" }
    },
    {
      title: "En attente",
      value: stats.pending,
      icon: Clock,
      color: "warning",
      badge: { text: "À délivrer", icon: <Clock className="me-1" size={12} /> },
      progress: { value: stats.pendingRate, label: "Proportion" }
    },
    {
      title: "Délivrées",
      value: stats.dispensed,
      icon: CheckCircle,
      color: "info",
      badge: { text: "Délivrées", icon: <CheckCircle className="me-1" size={12} /> },
      progress: { value: stats.dispensedRate, label: "Proportion" }
    },
    {
      title: "Remboursées",
      value: stats.reimbursed,
      icon: GraphUp,
      color: "success",
      badge: { text: "Remboursées", icon: <CheckCircle className="me-1" size={12} /> },
      progress: { value: stats.reimbursedRate, label: "Proportion" }
    }
  ];

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête */}
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <ol className="breadcrumb fs-sm mb-1">
                  <li className="breadcrumb-item"><a href="/patient">Tableau de bord</a></li>
                  <li className="breadcrumb-item active">Ordonnances</li>
                </ol>
                <h4 className="main-title mb-0">
                  <Prescription className="me-2" />
                  Historique des ordonnances
                </h4>
                <p className="text-muted mb-0">
                  Consultez l'historique complet de vos prescriptions médicales
                </p>
              </div>
            </div>
          </Col>
        </Row>

        {/* Cartes de statistiques - Style AgentsList */}
       <Row className="g-2 mb-4">
  {statsCards.map((card, idx) => (
    <Col md={6} lg={3} xs={6} key={idx}>
      <Card className="border-0 shadow-sm overflow-hidden stats-card h-100  bg-white">
        <div className={`card-gradient bg-${card.color}`} style={{ height: '3px' }}></div>
        <Card.Body className="p-3 d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="text-uppercase text-muted mb-1 small fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                {card.title}
              </h6>
              <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                {card.value}
              </h3>
              <div className="mt-1">
                <Badge bg={`${card.color}-subtle`} text={card.color} className="fw-medium d-inline-flex align-items-center" style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem' }}>
                  {card.badge.icon}
                  {card.badge.text}
                </Badge>
              </div>
            </div>
            {/* Optionnel : icône à droite si vous voulez */}
          </div>
          <div className="progress-wrapper mt-2">
            <div className="d-flex justify-content-between mb-0">
              <small className="text-muted" style={{ fontSize: '0.7rem' }}>{card.progress.label}</small>
              <small className="fw-medium" style={{ fontSize: '0.7rem' }}>{Math.round(card.progress.value)}%</small>
            </div>
            <ProgressBar 
              now={card.progress.value} 
              variant={card.color}
              className="stats-progress"
              style={{ height: '4px' }}
            />
          </div>
        </Card.Body>
      </Card>
    </Col>
  ))}
</Row>
        {/* Filtres */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white">
              <Card.Body>
                <Row className="g-3">
                  <Col md={6} >
                    <InputGroup>
                      <InputGroup.Text>
                        <Search />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Rechercher par médecin, médicament ou numéro..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4} xs={6}>
                    <InputGroup>
                      <InputGroup.Text>
                        <Filter />
                      </InputGroup.Text>
                      <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="pending">En attente</option>
                        <option value="dispensed">Délivrées</option>
                        <option value="reimbursed">Remboursées</option>
                      </Form.Select>
                    </InputGroup>
                  </Col>
                  <Col md={2} xs={6}>
                    <Button 
                      variant="outline-secondary" 
                      className="w-100"
                      onClick={() => {
                        setFilterText('');
                        setStatusFilter('all');
                      }}
                    >
                      Réinitialiser
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tableau principal */}
        <Row>
          <Col md={12}>
            <Card className="shadow-sm border-0 bg-white">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center">
                  <FileText className="me-2" />
                  <span className="fw-semibold">
                    Ordonnances ({sortedPrescriptions.length})
                  </span>
                </div>
                <div className="text-white-50 small">
                  {filterText && `Filtré par: "${filterText}"`}
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <SortableHeader field="date">Date</SortableHeader>
                        <SortableHeader field="doctorName">Médecin</SortableHeader>
                        <th>Médicaments</th>
                        <th>Statut</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPrescriptions.length > 0 ? (
                        sortedPrescriptions.map(p => (
                          <tr key={p.id}>
                            <td className="small">{formatDate(p.date)}</td>
                            <td className="fw-medium">{p.doctorName}</td>
                            <td className="small text-muted">
                              {p.medicines.slice(0, 2).map(m => m.name).join(', ')}
                              {p.medicines.length > 2 && ` +${p.medicines.length - 2}`}
                            </td>
                            <td>{getStatusBadge(p.status)}</td>
                            <td>
                              <Button 
                                size="sm" 
                                variant="outline-primary"
                                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => {
                                  setSelectedPrescription(p);
                                  setShowDetailsModal(true);
                                }}
                              >
                                <Eye size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <Prescription size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune ordonnance trouvée</p>
                            {(filterText || statusFilter !== 'all') && (
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => {
                                  setFilterText('');
                                  setStatusFilter('all');
                                }}
                              >
                                Réinitialiser les filtres
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal détails */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <Prescription className="me-2" />
              Détails de l'ordonnance
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPrescription && (
              <>
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <small className="text-muted d-block">Numéro d'ordonnance</small>
                      <strong>{selectedPrescription.id}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <small className="text-muted d-block">Date de prescription</small>
                      <strong>{formatDate(selectedPrescription.date)}</strong>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <small className="text-muted d-block">Médecin prescripteur</small>
                      <strong>{selectedPrescription.doctorName}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <small className="text-muted d-block">Diagnostic</small>
                      <strong>{selectedPrescription.diagnosis || "Non spécifié"}</strong>
                    </div>
                  </Col>
                </Row>

                <h6 className="mb-3">Médicaments prescrits</h6>
                <Table bordered className="mb-4">
                  <thead className="bg-light">
                    <tr>
                      <th>Médicament</th>
                      <th>Posologie</th>
                      <th>Quantité</th>
                      <th>Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPrescription.medicines.map((m, idx) => (
                      <tr key={idx}>
                        <td><strong>{m.name}</strong></td>
                        <td>{m.dosage}</td>
                        <td>{m.quantity}</td>
                        <td className="small text-muted">{m.instruction || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {selectedPrescription.notes && (
                  <Alert variant="info" className="mb-3">
                    <Activity className="me-2" />
                    <strong>Notes du médecin :</strong> {selectedPrescription.notes}
                  </Alert>
                )}

                <Alert variant={selectedPrescription.status === 'pending' ? 'warning' : 'success'}>
                  <CheckCircle className="me-2" />
                  {selectedPrescription.status === 'pending' 
                    ? "Cette ordonnance est en attente de délivrance en pharmacie. Présentez-vous avec votre carte d'assuré."
                    : selectedPrescription.status === 'dispensed'
                    ? "Cette ordonnance a été délivrée. La demande de remboursement est en cours de traitement."
                    : "Cette ordonnance a été entièrement remboursée."}
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Fermer
            </Button>
            <Button variant="primary">
              <Download className="me-2" />
              Télécharger PDF
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 12px;
            overflow: hidden;
          }

          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
          }

          .card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }

          .icon-container {
            width: 56px;
            height: 56px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .stats-progress {
            height: 6px;
            border-radius: 3px;
          }

          .stats-progress .progress-bar {
            border-radius: 3px;
          }

          @media (max-width: 768px) {
            .stats-card .icon-container {
              width: 48px;
              height: 48px;
            }
            
            .stats-card h2 {
              font-size: 1.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PrescriptionHistory;